import { useCallback, useEffect, useState } from 'react';

import type { AppError } from '../../api/errors.js';
import type {
  EvaluationRun,
  IWorkflowApiService,
} from '../../services/workflowApi.interface.js';

export interface UseEvalRunsReturn {
  runs: EvaluationRun[];
  hasNextPage: boolean;
  isLoading: boolean;
  error: AppError | null;
  refresh: () => void;
}

const ALL_STATUSES = 'All statuses';

/**
 * Shape-check `YYYY-MM-DD` AND validate as a real calendar date so an
 * invalid deep-link value (e.g. `2026-99-99` or `2026-02-31`) can't reach
 * `new Date(...).toISOString()` and throw, and can't silently normalise to
 * a different day than the URL string claims.
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export function isValidYyyyMmDd(value: string): boolean {
  if (!ISO_DATE_RE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  return d.toISOString().slice(0, 10) === value;
}

export function useEvalRuns(
  service: IWorkflowApiService,
  workspaceName: string,
  page = 1,
  pageSize = 25,
  statusFilter = ALL_STATUSES,
  search = '',
  dateFilter = '',
): UseEvalRunsReturn {
  const [runs, setRuns] = useState<EvaluationRun[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AppError | null>(null);

  // Debounce the search value so we don't fire on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(() => {
    if (!workspaceName) return;
    setIsLoading(true);
    setError(null);

    const skip = (page - 1) * pageSize;
    const parts: string[] = [];
    if (statusFilter !== ALL_STATUSES)
      parts.push(`runState eq '${statusFilter}'`);
    if (debouncedSearch.trim()) {
      parts.push(
        `startswith(displayName,'${debouncedSearch.trim().replace(/'/g, "''")}')`,
      );
    }
    // Date filter is server-side so the page returns *all* runs for that
    // UTC day (drill-down from Trends can land on days that would otherwise
    // fall outside the default top-25 sorted-desc window and appear empty).
    // Window is widened to ±1 day because the Trends chart buckets by the
    // metric's StartTime UTC-day while runs are filtered by createdDate;
    // long-running evaluations can straddle midnight and land on adjacent
    // UTC days between the two fields.
    //
    // We shape-check with a regex AND round-trip through `Date` so that
    // impossible values (e.g. `2026-99-99`, `2026-02-31`) from a shared or
    // manually edited deep link are treated as absent rather than
    // throwing on `.toISOString()` or silently normalising to a
    // different day.
    if (isValidYyyyMmDd(dateFilter)) {
      const startDate = new Date(`${dateFilter}T00:00:00Z`);
      startDate.setUTCDate(startDate.getUTCDate() - 1);
      const endDate = new Date(`${dateFilter}T00:00:00Z`);
      endDate.setUTCDate(endDate.getUTCDate() + 2);
      const start = startDate.toISOString().replace(/\.\d+Z$/, 'Z');
      const end = endDate.toISOString().replace(/\.\d+Z$/, 'Z');
      parts.push(
        `systemProperties/createdDate ge ${start} and systemProperties/createdDate lt ${end}`,
      );
    }
    const filter = parts.length > 0 ? parts.join(' and ') : undefined;

    const fetch = async () => {
      try {
        const response = await service.listEvaluationRuns(workspaceName, {
          top: pageSize,
          skip,
          filter,
        });
        setRuns(response.value);
        setHasNextPage(response.nextLink != null);
      } catch (err: unknown) {
        setError(err as AppError);
      } finally {
        setIsLoading(false);
      }
    };

    void fetch();
  }, [
    service,
    workspaceName,
    page,
    pageSize,
    statusFilter,
    debouncedSearch,
    dateFilter,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  return { runs, hasNextPage, isLoading, error, refresh: load };
}
